import React, { useCallback, useEffect, useRef } from "react"
// eslint-disable-next-line no-restricted-imports
import { Container, FlexContainer, Grid, GridItem, useBreakpointValue } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import {
  DefaultThreadMessage,
  StyledThreadsList,
  ThreadCardContainer,
  ThreadSideTick,
  ThreadSubjectAndDate
} from "./Threads"
import { CenteredLoadingIndicator } from "../views/molecules/Molecules"
import { InAppMessagingCreateNewThreadForm, NewMessageButtonContainer } from "./NewThread"
import { MessagingErrorContainer, ThreadMessagesContainer } from "./Messages"
import { InAppMessagingThread } from "./inAppMessagingTypes"
import { MessageReplyContainer } from "./MessageReplyContainer"
import { useInAppMessaging } from "../context/messaging/InAppMessagingContext"
import useGetThreads from "./hooks/useGetThreads"
import { useHistory } from "react-router"
import { useParams } from "react-router-dom"
import { useIntl } from "react-intl"
import { Stack, useTheme, Box } from "@carrotfertility/carotene-core"
import { useCarrotCompanionCarrotPlanMessage } from "#/components/care-team/hooks/useCarrotCompanion"
import { updateCarrotPlanMessageDismissTimestamp } from "#/components/views/modal/utils/localStorageUtils"

function DefaultThreadCard(): JSX.Element {
  const { selectedThread, setSelectedThread, showNewThreadForm, setShowNewThreadForm, setShowOnlyThreadsList } =
    useInAppMessaging()
  const intl = useIntl()

  return (
    <Container stack="138px" sx={{ borderBlockEnd: "1px solid rgba(0, 0, 0, 0.1)" }}>
      <ThreadCardContainer
        key={-1}
        onClick={() => {
          // canned response
          setSelectedThread({ threadId: -1 })
          setShowNewThreadForm(false)
          setShowOnlyThreadsList(false)
        }}
      >
        <ThreadSideTick active={selectedThread?.threadId === -1 && !showNewThreadForm} />
        <ThreadSubjectAndDate
          thread={{
            date: intl.formatMessage({ defaultMessage: "From Carrot" }),
            subject: intl.formatMessage({ defaultMessage: "Get started with Messages" })
          }}
        />
      </ThreadCardContainer>
    </Container>
  )
}

function MemberMessagesContainer(): JSX.Element {
  const { thread: threadParam } = useParams<{ thread: string }>()
  const history = useHistory()
  const hideColumn = useBreakpointValue<boolean>([true, true, true, false])
  const intl = useIntl()

  const {
    showNewThreadForm,
    setShowNewThreadForm,
    setShowOnlyThreadsList,
    showOnlyThreadsList,
    selectedThread,
    setSelectedThread
  } = useInAppMessaging()

  const {
    data: threadsData,
    isLoading: isGetThreadsLoading,
    isError: isGetThreadsError,
    isSuccess: isGetThreadsSuccess
  } = useGetThreads()

  const { data: cpMessage } = useCarrotCompanionCarrotPlanMessage()

  const theme = useTheme()

  useEffect(() => {
    if (isGetThreadsSuccess && selectedThread == null && !showNewThreadForm) {
      if (threadParam) {
        const linkedThread = threadsData.threads.find((t) => t.threadId === parseInt(threadParam, 10))
        if (!linkedThread) {
          history.replace("/messages")
          setSelectedThread(threadsData.threads[0])
        }
        setSelectedThread(linkedThread)
      } else if (threadsData.threads[0]) {
        setSelectedThread(threadsData.threads[0])
      } else {
        setSelectedThread({ threadId: -1 })
      }
    }

    if (selectedThread?.threadId === cpMessage?.thread?.threadId) {
      updateCarrotPlanMessageDismissTimestamp()
    }
  }, [
    threadParam,
    selectedThread,
    setSelectedThread,
    showNewThreadForm,
    isGetThreadsSuccess,
    history,
    threadsData,
    cpMessage
  ])

  const mapThreads = useCallback(
    (threads: InAppMessagingThread[]) => {
      return threads.map((thread: InAppMessagingThread) => {
        return (
          <ThreadCardContainer
            key={thread.threadId}
            onClick={() => {
              setSelectedThread(thread)
              setShowNewThreadForm(false)
              setShowOnlyThreadsList(false)
            }}
          >
            <ThreadSideTick active={selectedThread?.threadId === thread.threadId && !showNewThreadForm} />
            <Stack
              direction="row"
              spacing={theme.spacing(theme.tokens.spacing.sm)}
              width="100%"
              sx={{ overflow: "hidden", backgroundColor: (theme) => theme.palette.background.paper }}
            >
              <ThreadSubjectAndDate
                thread={{
                  date: intl.formatDate(thread.updatedAt, { month: "short", day: "numeric" }),
                  subject: thread.subject
                }}
              />
              {thread.hasUnreadMessages && (
                <Box
                  sx={{
                    display: "flex",
                    inlineSize: "9px",
                    justifyContent: "flex",
                    alignItems: "center",
                    marginBlockStart: "4px"
                  }}
                >
                  <Box
                    sx={{
                      display: "block",
                      backgroundColor: (theme) => theme.palette.primary.accent,
                      blockSize: (theme) => theme.spacing(theme.tokens.spacing.xs),
                      inlineSize: (theme) => theme.spacing(theme.tokens.spacing.xs),
                      borderRadius: "50%"
                    }}
                  />
                </Box>
              )}
            </Stack>
          </ThreadCardContainer>
        )
      })
    },
    [
      selectedThread?.threadId,
      setSelectedThread,
      showNewThreadForm,
      setShowNewThreadForm,
      setShowOnlyThreadsList,
      intl,
      theme
    ]
  )

  const threadsListScrollRef = useRef(null)
  function scrollThreadsListToTop(): void {
    threadsListScrollRef.current.scrollIntoView()
  }

  return (
    <>
      {isGetThreadsLoading && <CenteredLoadingIndicator />}
      {isGetThreadsError && <MessagingErrorContainer />}
      {isGetThreadsSuccess && (
        <Grid
          gap="none"
          height={["calc(100vh - 219px)", "calc(100vh - 248px)", null, null]}
          templateRows={["1fr"]}
          templateColumns={["1fr", null, null, "1fr 2fr"]}
        >
          <Box
            sx={(theme) => ({
              minInlineSize: { xs: "100%", lg: "232px" },
              blockSize: "inherit",
              display: hideColumn && !showOnlyThreadsList ? "none" : "block",
              paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
              overflowY: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.tokens.borderRadius.md
            })}
          >
            <StyledThreadsList>
              <div ref={threadsListScrollRef} />
              <GridItem as="nav" colStart={1} colSpan={1} rowStart={1}>
                {mapThreads(threadsData.threads)}
                <DefaultThreadCard />
              </GridItem>
            </StyledThreadsList>
            <NewMessageButtonContainer
              disabled={showNewThreadForm}
              onClick={() => {
                setShowOnlyThreadsList(false)
                setShowNewThreadForm(true)
              }}
            />
          </Box>
          <Container
            minWidth={["100%", null, null, "385px"]}
            sx={{ display: hideColumn && showOnlyThreadsList ? "none" : "block" }}
          >
            <GridItem>
              {showNewThreadForm ? (
                <InAppMessagingCreateNewThreadForm
                  newestThread={!threadsData.threads[0]?.threadId ? { threadId: -1 } : threadsData.threads[0]}
                  scrollThreadsListToTop={scrollThreadsListToTop}
                />
              ) : selectedThread?.threadId === -1 ? (
                <DefaultThreadMessage />
              ) : (
                <FlexContainer
                  direction="column"
                  justifyContent="space-between"
                  height={["calc(100vh - 216px)", "calc(100vh - 248px)", null, null]}
                >
                  <Container
                    height="100%"
                    stack="small"
                    sx={{
                      overflowX: "hidden",
                      overflowY: "auto",
                      marginInlineStart: [false, null, null, "small"]
                    }}
                  >
                    <ThreadMessagesContainer />
                  </Container>
                  <MessageReplyContainer thread={selectedThread} scrollThreadsListToTop={scrollThreadsListToTop} />
                </FlexContainer>
              )}
            </GridItem>
          </Container>
        </Grid>
      )}
    </>
  )
}

export default MemberMessagesContainer
